// File: src/pages/business/JobCandidatesPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function JobCandidatesPage() {
  const { jobId } = useParams(); // ✅ Lấy Job ID từ URL
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [jobTitle, setJobTitle] = useState(''); // Để hiển thị tên Job trên header
  
  // State tìm kiếm & lọc
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // 1. FETCH DỮ LIỆU THEO JOB ID
  useEffect(() => {
    const fetchCandidatesByJob = async () => {
      try {
        // Gọi API mới tạo ở Bước 1
        const response = await fetch(`${API_URL}/api/candidates/job/${jobId}`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        
        // Format dữ liệu
        const formattedData = data.map(item => ({
          ...item,
          // Nếu có populate jobId thì lấy title, ko thì dùng position cũ
          position: item.jobId?.title || item.position, 
          date: new Date(item.appliedDate).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }));

        setCandidates(formattedData);
        
        // Lấy tên Job để hiển thị tiêu đề (Lấy từ ứng viên đầu tiên hoặc gọi API job detail)
        if (formattedData.length > 0 && formattedData[0].jobId?.title) {
            setJobTitle(formattedData[0].jobId.title);
        } else {
            // Fallback: Gọi API lấy chi tiết Job nếu chưa có ứng viên nào để lấy tên
            fetchJobDetail();
        }

      } catch (error) {
        console.error("Lỗi tải dữ liệu:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchJobDetail = async () => {
        try {
            const res = await fetch(`${API_URL}/api/jobs/${jobId}`);
            if(res.ok) {
                const data = await res.json();
                setJobTitle(data.title);
            }
        } catch(e) {}
    };

    if (jobId) fetchCandidatesByJob();
  }, [jobId, API_URL]);

  // --- CÁC HÀM XỬ LÝ (GIỮ NGUYÊN TỪ TRANG CŨ) ---
  
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bạn có chắc muốn xóa ứng viên này?')) return;
    try {
      await fetch(`${API_URL}/api/candidates/${id}`, { method: 'DELETE' });
      setCandidates(candidates.filter(c => c._id !== id));
    } catch (error) { console.error('Lỗi xóa:', error); }
  };

  const handleStatusChange = async (candidateId, newStatus) => {
    // Optimistic Update
    setCandidates(prev => prev.map(c => c._id === candidateId ? { ...c, status: newStatus } : c));
    try {
        await fetch(`${API_URL}/api/candidates/${candidateId}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: newStatus })
        });
    } catch (e) { console.error("Lỗi update status", e); }
  };

  const handleExport = () => {
    const headers = ["Tên", "Email", "Vị trí", "Điểm số", "Ngày nộp", "Trạng thái"];
    const rows = filteredCandidates.map(c => [`"${c.name}"`, `"${c.email}"`, `"${c.position}"`, c.score || "0", `"${c.date}"`, `"${c.status}"`]);
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const link = document.createElement("a"); link.setAttribute("href", encodeURI(csvContent)); link.setAttribute("download", `ung_vien_${jobId}.csv`); document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // --- Logic hiển thị ---
  const filteredCandidates = candidates.filter(candidate => {
    if (filterStatus !== 'All' && candidate.status !== filterStatus) return false;
    const searchLower = searchTerm.toLowerCase();
    return candidate.name.toLowerCase().includes(searchLower) || candidate.email.toLowerCase().includes(searchLower);
  });

  const getStatusClass = (status) => {
    switch (status) {
      case 'Phỏng vấn': return 'bg-[#eef2ff] text-[#4F46E5] border-[#4F46E5]/20';
      case 'Từ chối': return 'bg-red-50 text-red-600 border-red-100';
      case 'Chấm điểm xong': return 'bg-slate-100 text-gray-500 border-gray-200';
      case 'Chưa hoàn thành': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'Đạt': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-white text-gray-700 border-gray-200';
    }
  };
  const getScoreColor = (score) => (!score ? 'bg-gray-200' : score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-slate-400' : 'bg-slate-300');
  const getScoreText = (score) => (!score ? 'text-gray-400 font-normal' : score >= 80 ? 'text-green-600 font-bold' : 'text-gray-500 font-medium');

  if (loading) return <div className="p-8 text-center text-sm text-gray-500">Đang tải danh sách ứng viên...</div>;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[#FAFAFA] font-sans text-[#111827]">
      
      {/* HEADER */}
      <div className="px-4 sm:px-8 py-6 flex-shrink-0">
        <div className="max-w-[1400px] mx-auto w-full flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/business/jobs')} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <div>
                <h2 className="text-xl font-semibold tracking-tight">Danh sách ứng viên</h2>
                <p className="text-gray-500 mt-1 text-sm">Công việc: <span className="font-semibold text-indigo-600">{jobTitle || 'Đang tải...'}</span></p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
              <div className="relative group w-full sm:w-80">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><span className="material-symbols-outlined text-[#6B7280] text-[20px]">search</span></div>
                <input type="text" className="block w-full h-9 rounded-md border-0 py-1.5 pl-10 pr-12 ring-1 ring-inset ring-[#E5E7EB] placeholder:text-[#6B7280] focus:ring-2 focus:ring-inset focus:ring-[#4F46E5] sm:text-sm sm:leading-6 shadow-sm bg-white" placeholder="Tìm ứng viên..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
              </div>
              <div className="relative">
                <select className="h-8 pl-3 pr-8 rounded-md bg-white border border-[#E5E7EB] text-xs font-medium text-[#6B7280] hover:text-[#111827] focus:outline-none focus:ring-0 cursor-pointer appearance-none" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="All">Trạng thái: Tất cả</option>
                  <option value="Chưa hoàn thành">Chưa hoàn thành</option>
                  <option value="Phỏng vấn">Phỏng vấn</option>
                  <option value="Đạt">Đạt</option>
                  <option value="Từ chối">Từ chối</option>
                </select>
                <span className="material-symbols-outlined text-[14px] text-[#6B7280] absolute right-2 top-2 pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <button onClick={handleExport} className="flex items-center justify-center gap-2 h-9 px-3 bg-white border border-[#E5E7EB] rounded-md text-sm font-medium hover:bg-slate-50 transition-all shadow-sm text-[#111827]">
                <span className="material-symbols-outlined text-[18px]">file_upload</span> Xuất Excel
            </button>
          </div>
        </div>
      </div>

      {/* TABLE CONTENT */}
      <div className="flex-1 overflow-auto px-4 sm:px-8 pb-8"> 
        <div className="max-w-[1400px] mx-auto w-full h-full flex flex-col">
          <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-card flex flex-col h-full overflow-hidden">
            <div className="overflow-x-auto flex-1">
              <table className="min-w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 border-b border-[#E5E7EB] sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#6B7280] w-[50px] text-center">#</th>
                    <th className="px-4 py-3 font-medium text-[#6B7280] w-[250px]">Ứng viên</th>
                    <th className="px-4 py-3 font-medium text-[#6B7280] w-[180px]">Điểm đánh giá</th>
                    <th className="px-4 py-3 font-medium text-[#6B7280] w-[150px]">Ngày nộp</th>
                    <th className="px-4 py-3 font-medium text-[#6B7280] w-[180px]">Trạng thái</th>
                    <th className="px-4 py-3 font-medium text-[#6B7280] text-right w-[100px]">Hành động</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB] bg-white">
                  {filteredCandidates.length > 0 ? filteredCandidates.map((candidate, index) => (
                    <tr key={candidate._id} className="group hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-2.5 text-center text-[#6B7280]">{index + 1}</td>
                      <td className="px-4 py-2.5">
                        <div 
                          className="flex items-center gap-3 cursor-pointer group-hover:opacity-80"
                          onClick={() => navigate(`/business/candidates/${candidate._id}`)}
                        >
                          {candidate.img ? (
                            <img alt={candidate.name} className="h-8 w-8 rounded-full border border-slate-200 object-cover" src={candidate.img}/>
                          ) : (
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border border-white shadow-sm bg-gray-100 text-gray-600`}>
                              {candidate.name ? candidate.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-[#111827] hover:text-[#4F46E5] transition-colors">{candidate.name}</div>
                            <div className="text-xs text-[#6B7280]">{candidate.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 text-right ${getScoreText(candidate.score)} text-sm`}>{candidate.score || '-'}</span>
                          <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${getScoreColor(candidate.score)}`} style={{ width: `${candidate.score || 0}%` }}></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280]">{candidate.date}</td>
                      <td className="px-4 py-2.5">
                        <select 
                          value={candidate.status}
                          onChange={(e) => handleStatusChange(candidate._id, e.target.value)}
                          className={`block w-full py-0.5 pl-2.5 pr-6 rounded-full border text-xs font-medium shadow-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] cursor-pointer ${getStatusClass(candidate.status)}`}
                          style={{ height: '26px' }}
                        >
                          <option value="Chưa hoàn thành">Chưa hoàn thành</option>
                          <option value="Chấm điểm xong">Chấm điểm xong</option>
                          <option value="Phỏng vấn">Phỏng vấn</option>
                          <option value="Đạt">Đạt</option>
                          <option value="Từ chối">Từ chối</option>
                        </select>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => navigate(`/business/candidates/${candidate._id}`)} className="p-1.5 text-gray-500 hover:text-[#4F46E5] hover:bg-indigo-50 rounded-md transition-colors"><span className="material-symbols-outlined text-[20px]">visibility</span></button>
                          <button onClick={(e) => handleDelete(candidate._id, e)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-500 italic">Chưa có ứng viên nào ứng tuyển vào vị trí này.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        select { -webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E"); background-position: right 0.4rem center; background-repeat: no-repeat; background-size: 1.2em 1.2em; }
      `}</style>
    </div>
  );
}